/**
 * Resuelve paths de assets de tema para el browser.
 * - `content/...` (o URL CDN absoluta que embeba `/content/`) → experience API
 * - URL ya apuntando a `/content/object?path=` → reusa el query `path` (y reescribe base)
 * - `assets/...` → path local de la app
 * - otros `http(s)://` → se dejan igual
 */

export type ResolveThemeAssetOptions = {
    experienceApiBaseUrl?: string;
    /** @deprecated Prefer experienceApiBaseUrl; CDN no se usa para theme assets. */
    contentVersion?: string;
    cdnBaseUrl?: string;
    storagePrefix?: string;
};

export type ThemeAssetOverrides = {
    LOGO_URL?: string;
    LOGIN_BACKGROUND_URL?: string;
};

export function resolveThemeAssetUrl(
    raw: string | undefined,
    options: ResolveThemeAssetOptions = {},
): string | undefined {
    if (!raw) {
        return raw;
    }
    const trimmed = raw.trim();
    if (!trimmed) {
        return trimmed;
    }

    const contentPath = extractContentRelativePath(trimmed);
    const apiBase = (options.experienceApiBaseUrl || '').replace(/\/$/, '');
    if (contentPath && apiBase) {
        return `${apiBase}/content/object?path=${encodeURIComponent(contentPath)}`;
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        return trimmed;
    }
    return trimmed;
}

/** `content/...` relative, CDN `/content/...`, or experience `/content/object?path=`. */
export function extractContentRelativePath(urlOrPath: string): string | undefined {
    const trimmed = (urlOrPath || '').trim();
    if (!trimmed) {
        return undefined;
    }
    if (trimmed.startsWith('content/')) {
        const pathOnly = trimmed.split('?')[0];
        return isContentObjectEndpointPath(pathOnly) ? undefined : pathOnly;
    }

    const fromQuery = extractPathQueryParam(trimmed);
    if (fromQuery) {
        return fromQuery;
    }

    const marker = '/content/';
    const idx = trimmed.indexOf(marker);
    if (idx < 0) {
        return undefined;
    }
    const withContent = trimmed.slice(idx + 1).split('?')[0];
    // Do not treat API route `/content/object` as an asset classpath path.
    if (isContentObjectEndpointPath(withContent)) {
        return undefined;
    }
    return withContent.startsWith('content/') ? withContent : undefined;
}

function isContentObjectEndpointPath(path: string): boolean {
    return path === 'content/object' || path.startsWith('content/object/');
}

/** Reads `path` from experience content/object URLs (absolute or relative). */
function extractPathQueryParam(urlOrPath: string): string | undefined {
    if (!urlOrPath.includes('content/object') || !urlOrPath.includes('path=')) {
        return undefined;
    }
    try {
        const url = new URL(urlOrPath, 'http://dummy.local');
        if (!url.pathname.includes('/content/object')) {
            return undefined;
        }
        const path = url.searchParams.get('path')?.trim();
        if (!path || !path.startsWith('content/') || isContentObjectEndpointPath(path)) {
            return undefined;
        }
        return path.split('?')[0];
    } catch {
        return undefined;
    }
}

/**
 * Maps raw Experience theme asset entries to browser-ready URLs
 * (same contract used by ExperienceThemeService.pickKnownAssets).
 */
export function resolveThemeAssetMap(
    raw: Record<string, string> | ThemeAssetOverrides | undefined,
    options: ResolveThemeAssetOptions = {},
): ThemeAssetOverrides {
    if (!raw) {
        return {};
    }
    const next: ThemeAssetOverrides = {};
    if (typeof raw.LOGO_URL === 'string' && raw.LOGO_URL) {
        next.LOGO_URL = resolveThemeAssetUrl(raw.LOGO_URL, options) ?? raw.LOGO_URL;
    }
    if (typeof raw.LOGIN_BACKGROUND_URL === 'string' && raw.LOGIN_BACKGROUND_URL) {
        next.LOGIN_BACKGROUND_URL =
            resolveThemeAssetUrl(raw.LOGIN_BACKGROUND_URL, options) ?? raw.LOGIN_BACKGROUND_URL;
    }
    return next;
}
